import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
const WorkspaceContext = createContext(undefined);
export const useWorkspace = () => {
    const context = useContext(WorkspaceContext);
    if (!context) {
        throw new Error('useWorkspace must be used within WorkspaceProvider');
    }
    return context;
};
export const WorkspaceProvider = ({ children }) => {
    const { isAuthenticated, isLoading: authLoading } = useAuth();
    const [workspaces, setWorkspaces] = useState([]);
    const [currentWorkspace, setCurrentWorkspace] = useState(null);
    const [members, setMembers] = useState([]);
    const [invites, setInvites] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const refreshWorkspaces = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const response = await fetch('/api/workspaces', {
                credentials: 'include',
            });
            if (!response.ok) {
                throw new Error('Failed to load workspaces');
            }
            const data = await response.json();
            const workspaceList = data.data || [];
            setWorkspaces(workspaceList);
            // Get current workspace from session/validation
            const validateResponse = await fetch('/api/auth/validate', {
                credentials: 'include',
            });
            if (validateResponse.ok) {
                const validateData = await validateResponse.json();
                const activeWorkspaceId = validateData.user?.activeWorkspaceId;
                if (activeWorkspaceId && workspaceList.length > 0) {
                    const active = workspaceList.find((w) => w.id === activeWorkspaceId);
                    setCurrentWorkspace(active || workspaceList[0]);
                }
                else if (workspaceList.length > 0) {
                    setCurrentWorkspace(workspaceList[0]);
                }
            }
        }
        catch (err) {
            setError(err.message || 'Failed to load workspaces');
            console.error('Workspace load error:', err);
        }
        finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        // Only load workspaces if user is authenticated
        if (isAuthenticated && !authLoading) {
            refreshWorkspaces();
        }
        else if (!authLoading) {
            // Not authenticated, clear loading state
            setIsLoading(false);
        }
    }, [isAuthenticated, authLoading]);
    const switchWorkspace = async (workspaceId) => {
        try {
            setError(null);
            const response = await fetch('/api/workspaces/switch', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ workspace_id: workspaceId }),
            });
            if (!response.ok) {
                throw new Error('Failed to switch workspace');
            }
            const data = await response.json();
            const workspace = data.workspace;
            setCurrentWorkspace(workspace);
            // Reload page to refresh all data with new workspace context
            window.location.reload();
        }
        catch (err) {
            setError(err.message || 'Failed to switch workspace');
            throw err;
        }
    };
    const createWorkspace = async (name, description) => {
        try {
            setError(null);
            const response = await fetch('/api/workspaces', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ name, description }),
            });
            if (!response.ok) {
                throw new Error('Failed to create workspace');
            }
            await refreshWorkspaces();
        }
        catch (err) {
            setError(err.message || 'Failed to create workspace');
            throw err;
        }
    };
    const updateWorkspace = async (workspaceId, name, description) => {
        try {
            setError(null);
            const response = await fetch(`/api/workspaces/${workspaceId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ name, description }),
            });
            if (!response.ok) {
                throw new Error('Failed to update workspace');
            }
            await refreshWorkspaces();
        }
        catch (err) {
            setError(err.message || 'Failed to update workspace');
            throw err;
        }
    };
    const loadMembers = async (workspaceId) => {
        try {
            const response = await fetch(`/api/workspaces/${workspaceId}/members`, {
                credentials: 'include',
            });
            if (!response.ok) {
                throw new Error('Failed to load members');
            }
            const data = await response.json();
            setMembers(data.data || []);
        }
        catch (err) {
            console.error('Load members error:', err);
            throw err;
        }
    };
    const loadInvites = async (workspaceId) => {
        try {
            const response = await fetch(`/api/workspaces/${workspaceId}/invites`, {
                credentials: 'include',
            });
            if (!response.ok) {
                throw new Error('Failed to load invites');
            }
            const data = await response.json();
            setInvites(data.data || []);
        }
        catch (err) {
            console.error('Load invites error:', err);
            throw err;
        }
    };
    const inviteMember = async (workspaceId, email, role) => {
        try {
            const response = await fetch(`/api/workspaces/${workspaceId}/invites`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ email, role }),
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to send invite');
            }
            await loadInvites(workspaceId);
        }
        catch (err) {
            throw err;
        }
    };
    const removeMember = async (workspaceId, userId) => {
        try {
            const response = await fetch(`/api/workspaces/${workspaceId}/members`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ user_id: userId }),
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to remove member');
            }
            await loadMembers(workspaceId);
        }
        catch (err) {
            throw err;
        }
    };
    const updateMemberRole = async (workspaceId, userId, role) => {
        try {
            const response = await fetch(`/api/workspaces/${workspaceId}/members`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ user_id: userId, role }),
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to update role');
            }
            await loadMembers(workspaceId);
        }
        catch (err) {
            throw err;
        }
    };
    const revokeInvite = async (inviteId) => {
        try {
            const response = await fetch(`/api/workspaces/${currentWorkspace?.id}/invites`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ invite_id: inviteId }),
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to revoke invite');
            }
            if (currentWorkspace) {
                await loadInvites(currentWorkspace.id);
            }
        }
        catch (err) {
            throw err;
        }
    };
    return (_jsx(WorkspaceContext.Provider, { value: {
            workspaces,
            currentWorkspace,
            isLoading,
            error,
            switchWorkspace,
            createWorkspace,
            updateWorkspace,
            refreshWorkspaces,
            members,
            invites,
            loadMembers,
            loadInvites,
            inviteMember,
            removeMember,
            updateMemberRole,
            revokeInvite,
        }, children: children }));
};
